import { useEffect, useState, useRef, useContext } from "react";
import { toast } from "react-toastify";

import api from "../../../BrandOnBoarding/api";
import { DiscoveryContext } from "../discovery-context";

export default function useHistory(email, type) {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState("");
  const [hasReachedEnd, setHasReachedEnd] = useState(false);

  const abortCtrlRef = useRef();
  const discoveryCtx = useContext(DiscoveryContext);

  useEffect(() => {
    if (abortCtrlRef.current) {
      abortCtrlRef.current.abort();
    }
    setHistory([]);
    setHasReachedEnd(false);
  }, [type]);

  async function getHistory(page = 1, limit = 5) {
    try {
      setError("");
      setLoading(true);
      if (abortCtrlRef.current) {
        abortCtrlRef.current.abort();
      }
      const abortCtrl = new AbortController();
      abortCtrlRef.current = abortCtrl;

      const response = await api.get(
        `/users/discovery?user_email=${encodeURIComponent(email)}&page=${page}&limit=${limit}&type=${type}`,
        { signal: abortCtrlRef.current.signal }
      );
      setLoading(false);
      if(response.status !== 200) {
        setError("Failed to fetch discovery")
        return;
      }
      const data = response.data;
      if (data.data.length === 0 || data.data.length < limit) {
        setHasReachedEnd(true);
      }
      const newHistory = data.data.map(item=>{
        return {
          ...item,
          market: item?.json_data?.market 
        }
      })
      setHistory((prev) => {
        return [...prev, ...newHistory];
      });
    } catch (error) {
      if (error.name === "CanceledError") {
        return;
      }
      setError("Failed to fetch discovery")
      setLoading(false);
    }
  }

  async function deleteHistory(id) {
    try {
      const response = await api.delete(`/users/discovery/${id}`);
      if (response.status === 200) {
        setHistory((prevHistory) => {
          const updatedHistory = prevHistory.filter(
            (history) => history.id !== id
          );
          return updatedHistory;
        });
      } else {
        toast.error("Failed to delete discovery");
      }
    } catch (error) {
      toast.error("Failed to delete discovery");
    }
  }

  async function updateHistory(id, name, json_data) {
    const itemIndex = history.findIndex((item) => item.id === id);
    try {
      const response = await api.patch(`/users/discovery/${id}`, { name, json_data });
      if (response.status !== 200) {
        toast.error("Failed to update discovery");
        throw error;
      }
      if (response.status === 200) {
        setHistory((prevHistory) => {
          const updatedHistory = [...prevHistory];
          if (itemIndex > -1) {
            updatedHistory[itemIndex] = {
              ...updatedHistory[itemIndex],
              name: name,
            };
          }
          return updatedHistory;
        });
      }
    } catch (error) {
      toast.error("Failed to update discovery");
      throw error;
    }
  }

  async function addHistory(data) {
    try {
      const response = await api.post(`/users/discovery`, data);
      if (response.status === 201) {
        discoveryCtx.updateId(response.data.data.id);
        setHistory((prevHistory) => {
          return [response.data.data, ...prevHistory];
        });
      } else {
        toast.error("Failed to save discovery");
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to save discovery");
    }
  }

  return {
    loading,
    error,
    history,
    getHistory,
    deleteHistory,
    updateHistory,
    addHistory,
    hasReachedEnd,
  };
}
